import { ApolloClient, NormalizedCacheObject } from '@apollo/client'

import {
  ExploreRegionDocument,
  RegionAdventuresDocument,
  RegionTrendingListingsDocument,
  RegionPlacePagesDocument,
} from 'hooks/useQueries'

type RegionSsr = {
  explore?: unknown
  adventures?: unknown
  trending?: unknown
  places?: unknown
}

type SeedRegion = {
  id?: string | number
}

type SeedMarket = {
  code?: string
}

// Variables must match the consumers (Region/index.tsx, Region/Adventures/index.tsx)
// exactly so the seed shares their Apollo cache key. The regionId/market code come from
// the same prehydrated currentRegion/currentMarket props the page reads. writeQuery is
// synchronous — safe in mini_racer SSR.
const seedRegion = (
  client: ApolloClient<NormalizedCacheObject>,
  ssr: RegionSsr | undefined,
  region: SeedRegion | undefined,
  market: SeedMarket | undefined,
): void => {
  if (!ssr || !region || !market) return

  const regionId = region.id?.toString()
  const marketCode = market.code

  if (ssr.explore && regionId && marketCode) {
    try {
      client.writeQuery({
        query: ExploreRegionDocument,
        variables: { market: marketCode, regionId },
        data: ssr.explore,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }

  if (ssr.adventures && regionId) {
    try {
      client.writeQuery({
        query: RegionAdventuresDocument,
        variables: { regionId },
        data: ssr.adventures,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }

  if (ssr.trending && regionId && marketCode) {
    try {
      client.writeQuery({
        query: RegionTrendingListingsDocument,
        variables: { market: marketCode, regionId },
        data: ssr.trending,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }

  if (ssr.places && regionId) {
    try {
      client.writeQuery({
        query: RegionPlacePagesDocument,
        variables: { regionId },
        data: ssr.places,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }
}

export default seedRegion
